import { useMutation, useQuery } from "@tanstack/react-query";
import type { GlossaryTerm } from "@unbox/api";
import { useMemo } from "react";
import { handleError } from "@/api/error";
import { client, useScopedMerchantClient } from "./scoped-merchant-client";

/** The metric glossary is static reference data, so it never goes stale within a session. */
export const useGlossary = (enabled = true) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["ask", scope.merchantId, scope.workspaceId, "glossary"],
    queryFn: async () => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].ask.glossary.$get({ param: scope.param })
        : await client.merchant[":merchantId"].ask.glossary.$get({ param: scope.param });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: enabled && !!scope.merchantId,
    staleTime: Number.POSITIVE_INFINITY,
  });
};

/**
 * Glossary terms keyed by lowercase term name AND alias, so a caller can look up
 * any surface form ("active creator", "active affiliate") and get the same term.
 */
export const useGlossaryTerms = (): Map<string, GlossaryTerm> => {
  const { data } = useGlossary();

  return useMemo(() => {
    const map = new Map<string, GlossaryTerm>();
    for (const family of data ?? []) {
      for (const term of family.terms) {
        map.set(term.term.toLowerCase(), term);
        for (const alias of term.aliases) {
          if (alias) map.set(alias.toLowerCase(), term);
        }
      }
    }
    return map;
  }, [data]);
};

export type AskFeedback = {
  session_id: string | null;
  message_id?: string | null;
  question: string;
  rating: 1 | -1;
  categories?: string[];
  correction?: string | null;
  tables_referenced?: string[];
  step_count?: number;
};

export const useSubmitAskFeedback = () => {
  const scope = useScopedMerchantClient();

  return useMutation({
    mutationFn: async (json: AskFeedback) => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].ask.feedback.$post({
            param: scope.param,
            json,
          })
        : await client.merchant[":merchantId"].ask.feedback.$post({ param: scope.param, json });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
  });
};
